import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const FitnessWizardContext = createContext();

const STORAGE_KEY = 'ph_draft_fitness';

export const useFitnessWizard = () => {
  const context = useContext(FitnessWizardContext);
  if (!context) {
    throw new Error('useFitnessWizard must be used within FitnessWizardProvider');
  }
  return context;
};

export const FitnessWizardProvider = ({ children, initialData }) => {
  const savedRaw = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  const saved = savedRaw ? JSON.parse(savedRaw) : null;
  const hasSaved = saved && saved.formData && saved.currentStep > 1 && !initialData;

  const [showResume, setShowResume] = useState(hasSaved);
  const [currentStep, setCurrentStep] = useState(1);
  const [draftId, setDraftId] = useState(saved?.draftId || '');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    applicant_email: '',
    applicant_phone: '',
    applicant_title: '',
    org_name: '',
    business_legal_name: '',
    business_dba: '',
    street_address: '',
    org_city: '',
    org_state: '',
    postal_code: '',
    year_business_started: '',
    website: '',
    fitness_club_type: '',
    fitness_club_type_other: '',
    fitness_services: [],
    fitness_services_other: '',
    location_type: 'Single',
    locations: [{ address: '', city: '', state: '', zip: '' }],
    facility_type: '',
    square_footage: '',
    hours_of_operation: '',
    active_members: '',
    average_daily_checkins: '',
    num_instructors: '',
    has_youth_participants: false,
    youth_participant_count: '',
    policy_effective_date: '',
    coverage_type: 'annual',
    policy_term_months: 12,
    gl_limit_per_occurrence: '$1,000,000',
    gl_limit_aggregate: '$2,000,000',
    requested_optional_coverages: [],
    claims_history_flag: false,
    claims_notes: '',
    claims_files: [],
    fraud_warranty_accepted: false,
    fraud_warranty_timestamp: null,
    additional_insured_required: false,
    additional_insured_details: '',
    risk_assessment_id: '',
    ...(initialData || {}),
  });

  const totalSteps = 10;

  useEffect(() => {
    if (currentStep > 0 && !showResume) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        formData,
        currentStep,
        draftId,
        savedAt: new Date().toISOString(),
      }));
    }
  }, [formData, currentStep, draftId, showResume]);

  const resumeDraft = useCallback(() => {
    if (saved) {
      setFormData(prev => ({ ...prev, ...saved.formData }));
      setCurrentStep(saved.currentStep || 1);
      if (saved.draftId) setDraftId(saved.draftId);
    }
    setShowResume(false);
  }, [saved]);

  const discardDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setShowResume(false);
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const updateFormData = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const goToStep = (step) => {
    if (step >= 1 && step <= totalSteps) setCurrentStep(step);
  };

  return (
    <FitnessWizardContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        formData,
        setFormData,
        updateFormData,
        nextStep,
        prevStep,
        goToStep,
        totalSteps,
        showResume,
        resumeDraft,
        discardDraft,
        clearDraft,
        draftId,
        setDraftId,
      }}
    >
      {children}
    </FitnessWizardContext.Provider>
  );
};
